import ReactPixel from 'react-facebook-pixel';
import { Mixpanel } from '../../constants.js/mixpanel';
import { getRememberedSource } from '../../utils/acquisitionSource';

/**
 * Analytics wrapper for signup v2. Every event carries `signup_variant: 'v2'`.
 * In demo mode nothing is sent: events are only logged to the console in development.
 */

export const SIGNUP_VARIANT = 'v2';
export const SIGNUP_FLOW_VERSION = '2026-10';

const isDev = process.env.NODE_ENV !== 'production';

const logDemo = (channel, name, props) => {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.info(`[SignUpV2 demo · ${channel}]`, name, props || '');
    }
};

const safely = (fn) => {
    try {
        fn();
    } catch (error) {
        if (isDev) {
            // eslint-disable-next-line no-console
            console.warn('[SignUpV2 analytics]', error);
        }
    }
};

export const SIGNUP_EXPERIMENT_NAME = 'signup_v2_onboarding';


/**
 * Where the visitor comes from (v1 and v2). It goes on `signup_viewed` (so funnels can break down by
 * it) and on the store's profile at signup, never as a super property: those are kept forever, and
 * a share weeks later would still carry the signup visit's UTMs. The API also keeps `source`.
 */
export const getSignupAcquisition = () => {
    const acquisition = {
        source: getRememberedSource(),
        referrer: document.referrer,
        landing_path: window.location.pathname,
        device: window.innerWidth <= 600 ? 'mobile' : 'desktop',
    };
    new URLSearchParams(window.location.search).forEach((value, key) => {
        if (key.indexOf('utm_') === 0) acquisition[key] = value;
    });
    return acquisition;
};

// Profile properties waiting for the store id: v1 only knows it after the panel loads (the panel
// identifies the store and calls `flushPendingSignupProfile`). Memory first, sessionStorage as backup.
const PENDING_PROFILE_KEY = 'ecometri-signup-profile';
let pendingProfile = null;

export const setPendingSignupProfile = (props) => {
    pendingProfile = { ...(pendingProfile || {}), ...props };
    try {
        window.sessionStorage.setItem(PENDING_PROFILE_KEY, JSON.stringify(pendingProfile));
    } catch {
        /* storage unavailable */
    }
};

/** Sets the pending signup properties on the (already identified) Mixpanel profile, once. */
export const flushPendingSignupProfile = () =>
    safely(() => {
        let props = pendingProfile;
        if (!props) {
            const raw = window.sessionStorage.getItem(PENDING_PROFILE_KEY);
            props = raw ? JSON.parse(raw) : null;
        }
        pendingProfile = null;
        window.sessionStorage.removeItem(PENDING_PROFILE_KEY);
        if (props) Mixpanel.people.set_once(props);
    });

const mb = (bytes) => (typeof bytes === 'number' ? Math.round((bytes / 1048576) * 100) / 100 : null);

/**
 * One event per picture the merchant picks (logo, banner, category, product): what they chose and
 * what we prepared from it, or why it failed.
 * @param {{ track: Function }} analytics
 * @param {{ target: string, file: File, ready?: File, error?: Error, startedAt: number }} info
 */
export const trackImagePicked = (analytics, { target, file, ready, error, startedAt }) => {
    const original = {
        target,
        original_type: (file && file.type) || 'unknown',
        original_ext: ((file && file.name) || '').split('.').length > 1 ? file.name.split('.').pop().toLowerCase() : '',
        original_mb: mb(file && file.size),
        duration_ms: Date.now() - startedAt,
    };
    if (error) {
        analytics.track('onboarding_photo_failed', { ...original, reason: (error && error.message) || 'unknown' });
        return;
    }
    analytics.track('onboarding_photo_added', {
        ...original,
        output_type: ready && ready.type,
        output_mb: mb(ready && ready.size),
        converted: !!ready && ready !== file,
    });
};

/**
 * Reports the A/B exposure for a `/sign-up` visit (v1 or v2). Call once per page load.
 * - If this browser still holds the identity of a previous store (logged out or expired session),
 *   resets Mixpanel first so the new signup is not attributed to that store.
 * - Registers `signup_variant` + `signup_variant_source` as super-properties, so every later signup
 *   event of this visitor carries the A/B arm.
 * - Tracks `signup_viewed` (common top of the funnel for both variants) with the acquisition data.
 * - Tracks Mixpanel's `$experiment_started` (Experiments report) only when the random split
 *   assigns this browser for the first time; links with `?signup=` are a pilot, not the test.
 * @param {{ variant: 'v1' | 'v2', source: string, isNew: boolean }} assignment
 * @param {boolean} isDemo when true, nothing is sent
 */
/**
 * Visit to `/sign-up/discount` (plan campaign page). It reuses the v1 form but isn't part of the A/B,
 * so it has no variant: `signup_page: 'discount'` tells it apart from `/sign-up` visits.
 */
export const trackDiscountSignupView = () =>
    safely(() => Mixpanel.track('signup_viewed', { signup_page: 'discount', ...getSignupAcquisition() }));

export const trackSignupVariantExposure = ({ variant, source, isNew }, isDemo) => {
    const props = { signup_variant: variant, signup_variant_source: source };
    if (isDemo) return logDemo('mixpanel', 'exposure', { ...props, is_new: isNew });

    return safely(() => {
        if (Mixpanel.get_property('$user_id')) Mixpanel.reset();
        Mixpanel.register(props);
        Mixpanel.track('signup_viewed', { ...props, signup_page: 'sign-up', ...getSignupAcquisition() });
        if (isNew && source === 'random') {
            Mixpanel.track('$experiment_started', {
                'Experiment name': SIGNUP_EXPERIMENT_NAME,
                'Variant name': variant,
            });
        }
    });
};

/**
 * @param {boolean} isDemo when true, nothing is sent
 */
export const createOnboardingAnalytics = (isDemo) => {
    const baseProps = { signup_variant: SIGNUP_VARIANT };

    return {
        /** v2 event (snake_case). */
        track: (name, props = {}) => {
            const payload = { ...baseProps, ...props };
            if (isDemo) return logDemo('mixpanel', name, payload);
            return safely(() => Mixpanel.track(name, payload));
        },
        register: (extra = {}) => {
            const props = {
                signup_variant: SIGNUP_VARIANT,
                signup_flow_version: SIGNUP_FLOW_VERSION,
                ...extra,
            };
            if (isDemo) return logDemo('mixpanel', 'register', props);
            return safely(() => Mixpanel.register(props));
        },
        identify: (storeId, peopleProps) => {
            if (isDemo) return logDemo('mixpanel', 'identify', { storeId, ...peopleProps });
            return safely(() => {
                Mixpanel.identify(storeId);
                Mixpanel.people.set_once(peopleProps);
            });
        },
        gaEvent: (params) => {
            if (isDemo) return logDemo('ga', params.action, params);
            // Same params as `lib/gtag` event() plus the variant.
            return safely(() => {
                if (typeof window.gtag !== 'function') return;
                window.gtag('event', params.action, {
                    event_category: params.category,
                    event_label: params.label,
                    value: params.value,
                    signup_variant: SIGNUP_VARIANT,
                });
            });
        },
        pixel: (name, props) => {
            if (isDemo) return logDemo('pixel', name, props);
            return safely(() => ReactPixel.track(name, props));
        },
    };
};
